import AvatarFallback from "./AvatarFallback";
import React from "react";
import { Box, Typography } from "@mui/material";
import { MoreHorizontalIcon } from "../AdaptiveIcon";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import RepeatRoundedIcon from "@mui/icons-material/RepeatRounded";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";

// Threads media grid (mirrors the Twitter/X layout, up to 4 shown). `images` are <ImageError> elements; only
// their `src` is used.
// `fluid` fits the grid to its column (replies sit next to the avatar line) instead of the fixed 523px.
const ThreadsMediaGrid = ({ images, fluid = false }) => {
  const list = Array.isArray(images) ? images : [];
  const numImages = list.length;
  if (numImages === 0) return null;
  const getGridStyle = () => {
    if (numImages === 2) {
      return { gridTemplateColumns: "1fr 1fr", gridTemplateRows: "1fr" };
    }
    if (numImages >= 3) {
      return { gridTemplateColumns: "1fr 1fr", gridTemplateRows: "1fr 1fr" };
    }
    return { gridTemplateColumns: "1fr", gridTemplateRows: "1fr" };
  };
  return (
    <Box className="flex justify-center mb-3">
      <Box
        className="media-preview threads-gallery"
        style={{ width: fluid ? "100%" : "523px", height: "auto", maxHeight: "523px" }}>
        <Box
          className="threads-gallery-grid"
          style={{
            display: "grid",
            gap: "2px",
            ...getGridStyle(),
            position: "relative",
          }}>
          {numImages === 1 ? (
            <img
              src={list[0].props.src}
              alt=""
              style={{
                width: "100%",
                height: "auto",
                maxHeight: "450px",
                objectFit: "cover",
                borderRadius: "12px",
                border: "1px solid var(--border-color)",
              }}
            />
          ) : numImages === 2 ? (
            list.slice(0, 2).map((image, index) => (
              <img
                key={index}
                src={image.props.src}
                alt=""
                style={{
                  width: "100%",
                  height: "280px",
                  objectFit: "cover",
                  borderRadius: index === 0 ? "12px 0 0 12px" : "0 12px 12px 0",
                  border: "1px solid var(--border-color)",
                }}
              />
            ))
          ) : numImages === 3 ? (
            <>
              <img
                src={list[0].props.src}
                alt=""
                style={{
                  width: "100%",
                  height: "100%",
                  minHeight: "280px",
                  objectFit: "cover",
                  gridRow: "1 / 3",
                  borderRadius: "12px 0 0 12px",
                  border: "1px solid var(--border-color)",
                }}
              />
              {list.slice(1, 3).map((image, index) => (
                <img
                  key={index + 1}
                  src={image.props.src}
                  alt=""
                  style={{
                    width: "100%",
                    height: "139px",
                    objectFit: "cover",
                    borderRadius: index === 0 ? "0 12px 0 0" : "0 0 12px 0",
                    border: "1px solid var(--border-color)",
                  }}
                />
              ))}
            </>
          ) : (
            list.slice(0, 4).map((image, index) => (
              <img
                key={index}
                src={image.props.src}
                alt=""
                style={{
                  width: "100%",
                  height: "139px",
                  objectFit: "cover",
                  borderRadius:
                    index === 0
                      ? "12px 0 0 0"
                      : index === 1
                      ? "0 12px 0 0"
                      : index === 2
                      ? "0 0 0 12px"
                      : "0 0 12px 0",
                  border: "1px solid var(--border-color)",
                }}
              />
            ))
          )}
        </Box>
      </Box>
    </Box>
  );
};

// One reply of a chain in the preview: the author's avatar with the thread line, the text and the media.
const ThreadsReply = ({ reply, author, isLast }) => {
  const userName = author ? author.username : "";
  return (
    <Box data-testid="threads-preview-reply" sx={{ display: "flex", gap: "10px", marginTop: "4px" }}>
      <Box sx={{ width: "36px", flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center" }}>
        {/* The line comes down from the previous post into this avatar. */}
        <Box sx={{ width: "2px", height: "14px", backgroundColor: "var(--border-color)" }} />
        <Box sx={{ width: "36px", height: "36px", borderRadius: "50%", overflow: "hidden", flexShrink: 0 }}>
          <AvatarFallback
            src={author ? author.avatar : null}
            name={userName}
            background="#000000"
            className="w-full h-full object-cover"
            style={{ borderRadius: "50%" }}
          />
        </Box>
        {!isLast && <Box sx={{ width: "2px", flex: 1, minHeight: "16px", backgroundColor: "var(--border-color)" }} />}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, paddingTop: "14px" }}>
        <Typography sx={{ color: "var(--text-color)", fontWeight: 700, fontSize: "15px" }} noWrap>
          {userName}
          <Box component="span" sx={{ color: "#999999", fontWeight: 400, fontSize: "13px", marginLeft: "6px" }}>
            1m
          </Box>
        </Typography>
        {reply.text && (
          <Typography
            component="div"
            style={{
              whiteSpace: "pre-wrap",
              overflow: "hidden",
              margin: "2px 0 8px",
              color: "var(--text-color)",
              fontSize: "15px",
              lineHeight: "1.5",
              overflowWrap: "anywhere",
            }}>
            {reply.text}
          </Typography>
        )}
        <ThreadsMediaGrid images={reply.images} fluid />
      </Box>
    </Box>
  );
};

const Threads = ({
  selectedAccounts,
  formattedTags,
  accResp,
  selectedImages,
  selectedIndex,
  avatars,
  postTitle,
  postText,
  replies,
}) => {
  const replyList = Array.isArray(replies) ? replies : [];
  const author = accResp.find((acc) => acc && acc.id === selectedAccounts[selectedIndex]) || null;
  return (
    <Box className="tab-pane h-full">
      <Box className="user-container p-10 relative mx-5">
        <Box
          className="instHeader mt-4"
          sx={{
            height: "20px",
            position: "relative",
            marginBottom: selectedAccounts.length === 0 ? "0px" : "30px",
          }}>
          {selectedAccounts.length === 0 ? (
            <Box
              className="avatar"
              sx={{
                width: "50px",
                height: "50px",
                borderRadius: "50%",
                backgroundColor: "var(--background-preview)",
                position: "absolute",
              }}
            />
          ) : (
            <>
              {selectedAccounts.map((id, i) => {
                const index = accResp.findIndex((acc) => acc.id === id);
                const avatar = avatars[index];
                const userName = accResp[index].username;

                return (
                  <Box
                    key={id}
                    className="author-img"
                    sx={{
                      backgroundColor: "var(--background-preview)",
                      position: "absolute",
                      display: i === selectedIndex ? "flex" : "none",
                      alignItems: "center",
                    }}>
                    <AvatarFallback
                      src={avatar}
                      name={userName}
                      background="#000000"
                      className="w-full h-full object-cover mr-2"
                      style={{ borderRadius: "50%" }}
                    />
                    <Box className="flex flex-col">
                      <Typography
                        className="whitespace-nowrap"
                        sx={{
                          marginLeft: "10px",
                          color: "var(--text-color)",
                          fontWeight: 700,
                          fontSize: "15px",
                        }}>
                        {userName}
                      </Typography>
                      <Typography
                        className="whitespace-nowrap"
                        sx={{
                          marginLeft: "10px",
                          color: "#999999",
                          fontSize: "13px",
                        }}>
                        @{userName} · 1m
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </>
          )}
          <Box className="absolute right-0">
            <MoreHorizontalIcon fontSize="large" />
          </Box>
        </Box>
        <Box
          className="user-info"
          style={{ position: "relative", paddingLeft: "60px", top: "-25px" }}>
          {selectedAccounts.length === undefined ||
          selectedAccounts.length === 0 ? (
            <>
              <Box
                style={{
                  width: "30%",
                  height: "12px",
                  backgroundColor: "var(--background-preview)",
                  marginBottom: "5px",
                }}
              />
              <Box
                style={{
                  width: "10%",
                  height: "12px",
                  backgroundColor: "var(--background-preview)",
                  marginBottom: "5px",
                }}
              />
            </>
          ) : null}
        </Box>

        {(postTitle === undefined || postTitle === "") &&
        selectedImages.length === 0 ? (
          <Box className="flex flex-col mb-8">
            <Box sx={{ width: "100%", display: "block", alignSelf: "center" }}>
              <Box
                style={{
                  width: "100%",
                  height: "12px",
                  backgroundColor: "var(--background-preview)",
                  marginBottom: "5px",
                }}></Box>
              <Box
                style={{
                  width: "30%",
                  height: "12px",
                  backgroundColor: "var(--background-preview)",
                  marginBottom: "5px",
                }}></Box>
            </Box>
          </Box>
        ) : (
          <Box className="mb-3">
            <Typography
              component="div"
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                marginBottom: "5px",
                color: "var(--text-color)",
              }}>
              {postTitle}
            </Typography>
          </Box>
        )}

        {postText === undefined || postText === "" ? (
          <Box className="flex flex-col mb-2">
            <Box sx={{ width: "85%", display: "block", alignSelf: "center" }}>
              <Box
                style={{
                  width: "100%",
                  height: "12px",
                  backgroundColor: "var(--background-preview)",
                  marginBottom: "5px",
                }}></Box>
              <Box
                style={{
                  width: "70%",
                  height: "12px",
                  backgroundColor: "var(--background-preview)",
                  marginBottom: "5px",
                }}></Box>
              <Box
                style={{
                  width: "50%",
                  height: "12px",
                  backgroundColor: "var(--background-preview)",
                  marginBottom: "5px",
                }}></Box>
            </Box>
          </Box>
        ) : (
          <>
            <Typography
              component="div"
              style={{
                whiteSpace: "pre-wrap",
                overflow: "hidden",
                marginBottom: "5px",
                color: "var(--text-color)",
                fontSize: "15px",
                lineHeight: "1.5",
              }}>
              {postText}
            </Typography>
            <Typography
              component="div"
              style={{
                whiteSpace: "pre-wrap",
                overflow: "hidden",
                marginBottom: "5px",
                color: "#3897f0",
                fontSize: "15px",
              }}>
              {formattedTags}
            </Typography>
          </>
        )}

        <ThreadsMediaGrid images={selectedImages} />
        {/* Threads action bar: comment, repost, like, share (grouped left) */}
        <Box
          className="threads-actions"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "18px",
            marginTop: "12px",
            paddingTop: "8px",
            color: "var(--text-color)",
          }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px", cursor: "pointer", "&:hover": { color: "#f91880" } }}>
            <FavoriteBorderIcon sx={{ fontSize: "20px" }} />
            <Typography sx={{ fontSize: "13px", color: "#999999" }}>147</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px", cursor: "pointer" }}>
            <ChatBubbleOutlineRoundedIcon sx={{ fontSize: "20px" }} />
            <Typography sx={{ fontSize: "13px", color: "#999999" }}>24</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px", cursor: "pointer" }}>
            <RepeatRoundedIcon sx={{ fontSize: "20px" }} />
            <Typography sx={{ fontSize: "13px", color: "#999999" }}>8</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
            <SendOutlinedIcon sx={{ fontSize: "20px" }} />
          </Box>
        </Box>

        {/* Parts 2..N of a chain: replies by the same author, joined by a line between the avatars. */}
        {replyList.map((reply, i) => (
          <ThreadsReply
            key={reply.key || i}
            reply={reply}
            author={author}
            isLast={i === replyList.length - 1}
          />
        ))}
      </Box>
    </Box>
  );
};

export default Threads;
